import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProject } from '@/contexts/ProjectContext';
import { motion } from 'framer-motion';
import { Search, ArrowRight, Check, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ProjectSelector() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { selectProject, setActiveProjects } = useProject();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [projectRoles, setProjectRoles] = useState({}); // projectId → role

  useEffect(() => {
    (async () => {
      try {
        // Load project access roles from login data
        const storedAccess = JSON.parse(localStorage.getItem('ciq_project_access') || '[]');
        const roleMap = {};
        storedAccess.forEach(pa => { roleMap[pa.projectId] = pa.projectRole; });
        setProjectRoles(roleMap);

        const res = await api.get('/projects/v2');
        const projs = res.data?.projects || [];
        // A superadmin is never a viewer: the seeded project grants must not
        // make the owner of the platform look read-only.
        const superadmin = ['SUPERADMIN', 'PLATFORM_ADMIN', 'admin'].includes(
          user?.platformRole || user?.role);
        const unique = [];
        const seen = new Set();
        projs.forEach(p => {
          const key = p.project_id || p.projectCode || p.name;
          if (seen.has(key)) return;
          seen.add(key);
          p.userRole = superadmin ? 'SUPERADMIN' : (roleMap[p.project_id] || p.userRole || 'VIEWER');
          unique.push(p);
        });
        setProjects(unique);
      } catch {
        try {
          const res = await api.get('/projects');
          setProjects(Array.isArray(res.data) ? res.data : res.data?.projects || []);
        } catch { setProjects([]); }
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = projects.filter(p => {
    const q = search.toLowerCase();
    const name = (p.projectName || p.name || '').toLowerCase();
    const code = (p.projectCode || '').toLowerCase();
    const client = (typeof p.client === 'object' ? p.client?.clientName : p.client || '').toLowerCase();
    return name.includes(q) || code.includes(q) || client.includes(q);
  });

  const toggleSelect = (pid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(p => p.project_id)));
  };

  const proceed = () => {
    if (selected.size === 0) { toast.error('Please select at least one project'); return; }

    const selectedIds = Array.from(selected);
    localStorage.setItem('ciq_active_projects', JSON.stringify(selectedIds));
    localStorage.setItem('ciq_show_portfolio', selected.size > 1 ? 'true' : 'false');

    // Update context with active projects
    setActiveProjects(selectedIds);

    // Auto-select first project for context
    selectProject(selectedIds[0]);

    // After picking projects, users land on the ultra-designer tile launcher
    // (no sidebar) where they choose which module to enter. The DashboardLayout
    // — with its full sidebar — assembles around them once they pick a tile.
    navigate('/launcher');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div className="w-10 h-10 border-2 border-[#1E4E8C] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen p-6 md:p-12" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--text-primary)' }} data-testid="project-selector-title">
              Select Your Projects
            </h1>
            <p className="mt-1" style={{ color: 'var(--text-muted)' }}>
              You have access to {projects.length} project{projects.length !== 1 ? 's' : ''}. Select the ones you want to work with.
            </p>
          </div>
          {(isAdmin?.() || user?.platformRole === 'PLATFORM_ADMIN') && (
            <Button onClick={() => navigate('/projects/create')} data-testid="create-project-btn"
              className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white">
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          )}
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-disabled)' }} />
            <input data-testid="project-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search projects by name, code, or client..."
              className="w-full pl-11 pr-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E4E8C]"
              style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
          </div>
          <button onClick={selectAll} className="text-sm px-3 py-2 rounded-lg whitespace-nowrap" data-testid="select-all-btn"
            style={{ color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
            {selected.size === filtered.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {/* Selection summary bar */}
        <div className="flex items-center justify-between mb-4 px-4 py-3 rounded-lg" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {selected.size} project{selected.size !== 1 ? 's' : ''} selected
            </span>
            {selected.size > 1 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E4E8C]/20 text-[#1E4E8C] font-medium">
                Portfolio Mode
              </span>
            )}
            {selected.size === 1 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#0E7C6B]/20 text-[#0E7C6B] font-medium">
                Single Project Mode
              </span>
            )}
            {/* Show roles for selected projects */}
            {selected.size > 0 && (() => {
              const selectedRoles = [...selected].map(pid =>
                (projects.find(p => p.project_id === pid)?.userRole) || projectRoles[pid] || 'VIEWER');
              const uniqueRoles = [...new Set(selectedRoles)];
              return (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Role{uniqueRoles.length > 1 ? 's' : ''}: {uniqueRoles.map(r => r.replace(/_/g, ' ')).join(', ')}
                </span>
              );
            })()}
          </div>
          <Button onClick={proceed} disabled={selected.size === 0} data-testid="proceed-btn"
            className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white disabled:opacity-50">
            {selected.size > 1 ? 'Open Portfolio' : selected.size === 1 ? 'Enter Project' : 'Select Projects'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((p, i) => {
            const name = p.projectName || p.name || '';
            const client = typeof p.client === 'object' ? p.client?.clientName : p.client || '';
            const geo = p.geography || `${p.location?.state || ''}, ${p.location?.country || ''}`.replace(/^, |, $/g, '');
            const role = p.userRole || 'VIEWER';
            const isSelected = selected.has(p.project_id);
            const code = p.projectCode || p.project_id;

            return (
              <motion.div key={p.project_id} data-testid={`project-card-${p.project_id}`}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                onClick={() => toggleSelect(p.project_id)}
                className={`rounded-xl px-4 py-3.5 cursor-pointer transition-all group relative ${isSelected ? 'ring-2 ring-[#1E4E8C]' : ''}`}
                style={{ backgroundColor: isSelected ? 'rgba(30,78,140,0.08)' : 'var(--card-bg)', border: '1px solid var(--border-color)' }}>

                {/* Checkbox */}
                <div className={`absolute top-3 right-3 w-5 h-5 rounded flex items-center justify-center transition-all ${isSelected ? 'bg-[#1E4E8C]' : ''}`}
                  style={isSelected ? {} : { border: '2px solid var(--border-color)' }}>
                  {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                </div>

                <div className="pr-7">
                  <h3 className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>{name}</h3>
                  <p className="text-xs truncate mt-1" style={{ color: 'var(--text-muted)' }}>
                    {[client, geo].filter(Boolean).join(' — ')}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: 'rgba(30,78,140,0.15)', color: '#5790C9' }}>{code}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--active-bg)', color: 'var(--text-muted)' }}>{role.replace(/_/g, ' ')}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {filtered.length === 0 && !loading && (
          <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>
            <p>No projects found matching your search.</p>
          </div>
        )}
      </div>
    </div>
  );
}
